import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import axios from 'axios';
import toast from 'react-hot-toast';
import { 
  PaperAirplaneIcon,
  ArrowLeftIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline';

const MessageThread = ({ conversation, onBack }) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  // Récupérer les messages de la conversation
  const { data: messages = [], isLoading } = useQuery(
    ['messages', conversation?.id],
    async () => {
      if (!conversation?.id) return [];
      const response = await axios.get(`/api/messages/conversations/${conversation.id}/messages`);
      return response.data;
    },
    {
      enabled: !!conversation?.id,
      refetchInterval: 5000 // Rafraîchir toutes les 5 secondes
    }
  );

  // Mutation pour envoyer un message
  const sendMessageMutation = useMutation(
    async (messageData) => {
      const response = await axios.post('/api/messages', messageData);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['messages', conversation.id]);
        queryClient.invalidateQueries('conversations');
        setNewMessage('');
        setSending(false);
      },
      onError: (error) => {
        console.error('Erreur envoi message:', error);
        toast.error('Erreur lors de l\'envoi du message');
        setSending(false);
      }
    }
  );

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    setSending(true);
    sendMessageMutation.mutate({
      conversationId: conversation.id,
      content: newMessage.trim(),
      recipientId: conversation.otherUser.id
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('fr-FR', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  if (!conversation) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-50">
        <div className="text-center">
          <ChatBubbleLeftRightIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">Sélectionnez une conversation</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center space-x-3">
          <button
            onClick={onBack}
            className="p-1 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 text-gray-600" />
          </button>
          
          <div className="flex items-center space-x-3">
            {conversation.otherUser.avatar ? (
              <img
                className="h-8 w-8 rounded-full"
                src={conversation.otherUser.avatar}
                alt={conversation.otherUser.firstName}
              />
            ) : (
              <div className="h-8 w-8 bg-primary-100 rounded-full flex items-center justify-center">
                <span className="text-sm font-medium text-primary-600">
                  {conversation.otherUser.firstName[0]}
                </span>
              </div>
            )}
            
            <div>
              <h3 className="text-sm font-medium text-text">
                {conversation.otherUser.firstName} {conversation.otherUser.lastName}
              </h3>
              <p className="text-xs text-gray-500">
                {conversation.otherUser.role === 'entrepreneur' ? 'Entrepreneur' : 
                 conversation.otherUser.role === 'investor' ? 'Investisseur' : 'Utilisateur'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500">Aucun message dans cette conversation</p>
            <p className="text-sm text-gray-400 mt-1">Envoyez le premier message !</p>
          </div>
        ) : (
          messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${message.senderId === user.id ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                  message.senderId === user.id
                    ? 'bg-primary-500 text-white'
                    : 'bg-gray-100 text-gray-900'
                }`}
              >
                <p className="text-sm">{message.content}</p>
                <p
                  className={`text-xs mt-1 ${
                    message.senderId === user.id
                      ? 'text-primary-100'
                      : 'text-gray-500'
                  }`}
                >
                  {formatTime(message.createdAt)}
                </p>
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-4 border-t border-gray-200">
        <form onSubmit={handleSendMessage} className="flex space-x-2">
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Tapez votre message..."
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
            disabled={sending}
          />
          <button
            type="submit"
            disabled={!newMessage.trim() || sending}
            className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {sending ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            ) : (
              <PaperAirplaneIcon className="h-4 w-4" />
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default MessageThread;
